import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('focusphoto_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('focusphoto_cart', JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((photo, price = 49) => {
    setItems(prev => {
      const existing = prev.find(item => item.photo_id === photo.id);
      if (existing) {
        return prev.map(item =>
          item.photo_id === photo.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        photo_id: photo.id,
        filename: photo.original_filename,
        thumbnail_url: photo.thumbnail_url || photo.url,
        price,
        quantity: 1
      }];
    });
  }, []);

  const removeItem = useCallback((photoId) => {
    setItems(prev => prev.filter(item => item.photo_id !== photoId));
  }, []);

  const updateQuantity = useCallback((photoId, quantity) => {
    if (quantity <= 0) {
      removeItem(photoId);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.photo_id === photoId ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
